import React from "react";
import { OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { BiPlus } from "react-icons/bi";
import { Link } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../app/hook";
import {
  useGetAllScenariosQuery,
  useSetDefaultScenariosMutation
} from "../../../app/services/chatbot-rtk.services";
import { IGetAllScenariosResponse } from "../../../app/services/chatbot-rtk.type";
import PopoverWithAudio from "../../../components/common/PopoverWithAudio";
import ApiFailure from "../../../components/ui/ApiFailure";
import ApiLoading from "../../../components/ui/ApiLoading";
import ProBadge from "../../../components/ui/ProBadge";
import { MultiLanguageUrl } from "../../../constants/multiLanguage.constant";
import { selectIsUserNew } from "../../../features/auth/auth.slice";
import {
  handleInitializeWizard,
  handleToggleCloneShareModal,
  handleToggleWizardModal
} from "../../../features/bot/botSlice";
import usePermission from "../../../hooks/usePermission";
import { BotCategoryEnum } from "../../../utils/BotCategoryEnum";
import { BotTypeEnum } from "../../../utils/BotTypeEnum";
import { renderStatusBadge } from "../knowledge-bases/knowledge-bases.page";

const LatestHumanClones = () => {
  const { t, i18n } = useTranslation();

  const dispatch = useAppDispatch();

  const permissions = usePermission();

  const isUserNew = useAppSelector(selectIsUserNew);

  const { isLoading, isSuccess, isError, refetch, data } =
    useGetAllScenariosQuery(
      { botType: "link" },
      {
        skip: isUserNew
      }
    );

  const [setDefaultScenarios, setDefaultScenariosStatus] =
    useSetDefaultScenariosMutation();

  const handleOpenCloneWizard = () => {
    dispatch(
      handleToggleWizardModal({
        status: true,
        category: "clone"
      })
    );

    dispatch(
      handleInitializeWizard({
        bot_code: BotTypeEnum.HUMAN_CLONE,
        bot_type: BotCategoryEnum.AI_BASED_CHATBOT
      })
    );
  };

  const handleIntegrateChatbot = (data: IGetAllScenariosResponse) => {
    dispatch(
      handleToggleCloneShareModal({
        status: true,
        data: { cloneId: data.id, logo: data.logo }
      })
    );
  };

  const popupAudioPath: { [key: string]: string } = {
    en: "https://cdn.1cdn.app/application/LOUISEBOT/2407031158281_humanclone_en.mp3",
    fr: "https://cdn.1cdn.app/application/LOUISEBOT/24070311582856_humanclone_fr.mp3"
  };

  let content;

  if (isLoading)
    content = (
      <div className="lg:tw-w-[399.33px] tw-w-full tw-h-[88px] tw-px-6 tw-py-[78px] tw-bg-white tw-rounded-lg tw-shadow tw-flex-col tw-justify-center tw-items-center tw-gap-4 tw-flex">
        <ApiLoading />
      </div>
    );

  if (isError)
    content = (
      <div className="lg:tw-w-[399.33px] tw-w-full tw-h-[88px] tw-px-6 tw-py-[78px] tw-bg-white tw-rounded-lg tw-shadow tw-flex-col tw-justify-center tw-items-center tw-gap-4 tw-flex">
        <ApiFailure refetch={refetch} />
      </div>
    );

  if (isSuccess)
    content = (
      <React.Fragment>
        {data && data.length > 0 ? (
          <div className="tw-flex lg:tw-flex-row tw-flex-col tw-flex-wrap tw-gap-3 tw-self-stretch">
            {[...data]
              .sort((a, b) => b.sessionUsage - a.sessionUsage)
              .slice(0, 3)
              .map((clone) => (
                <div
                  key={clone.id}
                  className="lg:tw-w-[399.33px] tw-w-full tw-p-6 tw-bg-white tw-rounded-lg tw-shadow tw-flex-col tw-justify-start tw-items-start tw-gap-6 tw-inline-flex"
                >
                  <div className="tw-self-stretch tw-justify-between tw-items-center tw-inline-flex">
                    <div className="tw-grow tw-shrink tw-basis-0 tw-self-stretch tw-justify-start tw-items-center tw-gap-2 tw-flex">
                      <div className="tw-relative">
                        <img
                          className="tw-w-10 tw-h-10 tw-rounded-[20px] tw-object-cover"
                          src={
                            clone.logo == ""
                              ? "https://cdn.1cdn.app/application/LOUISEBOT/2312171433313_Ellipse_980.png"
                              : clone.logo
                          }
                          alt=""
                        />
                        <OverlayTrigger
                          overlay={
                            <Tooltip>
                              {t("General/key3")}{" "}
                              {!clone.is_default
                                ? t("DashboardHeaderSection/deactivate")
                                : t("DashboardHeaderSection/activate")}
                            </Tooltip>
                          }
                        >
                          <div
                            className={`tw-w-4 tw-h-4 tw-absolute tw-right-0 tw-bottom-0 ${
                              !clone.is_default
                                ? "tw-bg-red-400"
                                : "tw-bg-emerald-400 blink"
                            } tw-rounded-full tw-shadow tw-border-2 tw-border-solid tw-border-white`}
                          />
                        </OverlayTrigger>
                      </div>
                      <div className="tw-grow tw-shrink tw-basis-0 tw-flex-col tw-justify-start tw-items-start tw-gap-0.5 tw-inline-flex">
                        <div className="tw-self-stretch tw-text-gray-700 tw-text-sm tw-font-medium tw-font-['Lexend'] tw-leading-tight">
                          {clone.title}

                          {renderStatusBadge(clone.status, t)}
                        </div>
                        <div className="tw-self-stretch tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                          {t("LatestHumanClones/key3")}
                        </div>
                      </div>
                    </div>
                    <button
                      className={`tw-border-0 tw-px-[15px] tw-py-[7px] ${"tw-bg-primary"} tw-rounded-[17px] tw-shadow tw-justify-center tw-items-center tw-flex`}
                      onClick={() => handleIntegrateChatbot(clone)}
                      disabled={setDefaultScenariosStatus.isLoading}
                    >
                      <div className="tw-text-white tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                        {setDefaultScenariosStatus.isLoading &&
                          setDefaultScenariosStatus.originalArgs ===
                            clone.id && (
                            <Spinner size="sm" className="tw-mr-2" />
                          )}
                        {t("LatestHumanClones/key4")}
                      </div>
                    </button>
                  </div>
                </div>
              ))}
            <div className="lg:tw-w-[399.33px] tw-w-full tw-h-[88px] tw-px-6 tw-p-6 tw-bg-white tw-rounded-lg tw-shadow tw-justify-center tw-items-center tw-flex">
              <button
                className="tw-border-0 tw-px-3 tw-py-2.5 tw-bg-white tw-rounded-lg tw-justify-center tw-items-center tw-gap-3 tw-flex"
                onClick={handleOpenCloneWizard}
                disabled={!permissions?.extensionPage?.addNewBot}
              >
                <div
                  className={`tw-p-1 ${
                    !permissions?.extensionPage?.addNewBot
                      ? "tw-bg-gray-400"
                      : "tw-bg-primary"
                  } tw-rounded-[19px] tw-shadow tw-justify-center tw-items-center tw-inline-flex`}
                >
                  <div className="tw-w-5 tw-h-5 tw-relative tw-flex tw-flex-col tw-items-center tw-justify-center">
                    <BiPlus size={22} className="tw-text-white" />
                  </div>
                </div>
                <div
                  className={`${
                    !permissions?.extensionPage?.addNewBot
                      ? "tw-text-gray-400"
                      : "tw-text-primary"
                  } tw-text-sm tw-font-semibold tw-font-['Lexend'] tw-leading-tight tw-flex tw-items-center tw-gap-2`}
                >
                  {t("LatestHumanClones/key5")}
                </div>
              </button>
              {!permissions?.extensionPage?.addNewBot && <ProBadge />}
            </div>
          </div>
        ) : (
          <div className="lg:tw-w-[399.33px] tw-w-full tw-h-[88px] tw-px-6 tw-bg-white tw-rounded-lg tw-shadow tw-justify-center tw-items-center tw-flex">
            <button
              className="tw-border-0 tw-px-3 tw-py-2.5 tw-bg-white tw-rounded-lg tw-justify-center tw-items-center tw-gap-3 tw-flex"
              onClick={handleOpenCloneWizard}
              disabled={!permissions?.extensionPage?.addNewBot}
            >
              <div
                className={`tw-p-1 ${
                  !permissions?.extensionPage?.addNewBot
                    ? "tw-bg-gray-400"
                    : "tw-bg-primary"
                } tw-rounded-[19px] tw-shadow tw-justify-center tw-items-center tw-inline-flex`}
              >
                <div className="tw-w-5 tw-h-5 tw-relative tw-flex tw-flex-col tw-items-center tw-justify-center">
                  <BiPlus size={22} className="tw-text-white" />
                </div>
              </div>
              <div
                className={`${
                  !permissions?.extensionPage?.addNewBot
                    ? "tw-text-gray-400"
                    : "tw-text-primary"
                } tw-text-sm tw-font-semibold tw-font-['Lexend'] tw-leading-tight tw-flex tw-items-center tw-gap-2`}
              >
                {t("LatestHumanClones/key5")}
              </div>
            </button>
            {!permissions?.extensionPage?.addNewBot && <ProBadge />}
          </div>
        )}
      </React.Fragment>
    );

  return (
    <div className="tw-w-full tw-px-6 tw-pt-6 tw-pb-1  tw-flex-col tw-justify-start tw-items-start tw-gap-4 tw-inline-flex">
      <div className="tw-self-stretch tw-justify-between tw-items-center tw-inline-flex tw-flex-wrap">
        <div className="tw-justify-start tw-items-center tw-gap-2 tw-flex tw-text-gray-700 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">
          {t("LatestHumanClones/key1")}
          <PopoverWithAudio
            data={{
              contentText: t("Tooltips/key2"),
              audioUrl: popupAudioPath[i18n.language]
            }}
          />
        </div>
        {isSuccess && data && data.length > 0 && (
          <Link
            to={`/${
              MultiLanguageUrl[i18n.language]
            }/control-panel/human-clones`}
            className="tw-text-blue-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal"
          >
            {t("LatestHumanClones/key2")}
          </Link>
        )}
      </div>

      {content}
    </div>
  );
};

export default LatestHumanClones;
